import { PARAMS, ROUTES } from "@src/common/constants";
import { getPublicWebAppOrigin } from "@src/utils/app-url";
import { useCallback, useMemo } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { WhiteboardDocumentId } from "../collaboration/document-id";

/**
 * Shared across every `useWhiteboardNavigation()` caller so back-navigation
 * works after leave/archive from a different component than the one that
 * navigated to the current board.
 */
let previousWhiteboardIds: string[] = [];

export function useWhiteboardId() {
	const whiteboardMatch = useMatch(`${ROUTES.whiteboards}/:id`);
	const id = whiteboardMatch?.params?.id;

	return useMemo(() => (id ? new WhiteboardDocumentId(id) : undefined), [id]);
}

/**
 * Typed whiteboard id string from the current route (`/whiteboards/:id`), or
 * `undefined` when not on that route. Prefer over raw `useMatch` + `params.id`.
 */
export function useWhiteboardRouteIdString(): string | undefined {
	const whiteboardId = useWhiteboardId();
	return whiteboardId?.toString();
}

function popPreviousWhiteboardId(availableWhiteboardIds: string[]) {
	if (previousWhiteboardIds.length === 0) {
		return;
	}

	for (let i = previousWhiteboardIds.length - 1; i >= 0; i--) {
		const previousId = previousWhiteboardIds[i];

		if (availableWhiteboardIds.includes(previousId)) {
			previousWhiteboardIds = previousWhiteboardIds.slice(0, i);
			return previousId;
		}
	}

	previousWhiteboardIds = [];
	return;
}

/**
 * Hook for navigating between whiteboards, including maintaining a history of visited whiteboards.
 */
export function useWhiteboardNavigation() {
	const navigate = useNavigate();
	const whiteboardId = useWhiteboardId();

	const navigateToWhiteboard = useCallback(
		(nextWhiteboardId: string) => {
			const currentId = whiteboardId?.toIdString();
			if (currentId && currentId !== nextWhiteboardId) {
				previousWhiteboardIds = [...previousWhiteboardIds, currentId];
			}

			navigate(`${ROUTES.whiteboards}/${nextWhiteboardId}`);
		},
		[navigate, whiteboardId],
	);

	const navigateBackToWhiteboard = useCallback(
		(
			availableWhiteboards: { id: string }[],
			excludedItemId?: string,
		): string | undefined => {
			const ids = availableWhiteboards
				.filter((whiteboard) => whiteboard.id !== excludedItemId)
				.map((whiteboard) => whiteboard.id);

			const previousWhiteboardId = popPreviousWhiteboardId(ids);

			if (previousWhiteboardId) {
				navigate(`${ROUTES.whiteboards}/${previousWhiteboardId}`);
				return previousWhiteboardId;
			}

			if (ids.length > 0) {
				navigate(`${ROUTES.whiteboards}/${ids[0]}`);
				return ids[0];
			}

			navigate(ROUTES.default);
			return undefined;
		},
		[navigate],
	);

	return {
		activeWhiteboardId: whiteboardId,
		navigate,
		navigateToWhiteboard,
		navigateBackToWhiteboard,
	};
}

const getPublishedUrl = (whiteboardId: string, shareKey: string | null) => {
	const url = new URL(
		`${getPublicWebAppOrigin()}${ROUTES.whiteboards}/${whiteboardId}`,
	);

	if (shareKey) {
		url.searchParams.set(PARAMS.shareKey, shareKey);
	}

	return url;
};

export const getPublishedLink = (
	whiteboardId: string,
	shareKey: string | null,
) => {
	return getPublishedUrl(whiteboardId, shareKey).href;
};

export const getShapeLink = (
	whiteboardId: string,
	shareKey: string | null,
	shapeId: string,
) => {
	const url = getPublishedUrl(whiteboardId, shareKey);
	url.searchParams.set(PARAMS.shapeId, shapeId);
	return url.href;
};
